import axios from "axios";

// Create a centralized Axios instance with default headers.
const api = axios.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

// Read the Rails CSRF token from the meta tag injected by csrf_meta_tags.
function getCsrfToken() {
  return document.querySelector("[name=csrf-token]")?.content;
}

// Function to check if force_trace is set in the URL
function shouldForceTrace() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("force_trace") === "true";
}

// Attach CSRF token + force_trace to every request from the custom instance.
api.interceptors.request.use(
  (config) => {
    const token = getCsrfToken();
    if (token) {
      config.headers["X-CSRF-Token"] = token;
    }

    if (shouldForceTrace()) {
      if (!config.params) {
        config.params = {};
      }
      config.params.force_trace = "true";
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Also set defaults on the global axios instance so that code importing
// axios directly (rather than this client) gets the same headers.
axios.defaults.headers.common["Accept"] = "application/json";
axios.interceptors.request.use(
  (config) => {
    // Only add CSRF token for requests to our own backend (relative URLs
    // or same-origin), not for third-party requests.
    const url = config.url || "";
    const isRelative = url.startsWith("/");
    const isSameOrigin = url.startsWith(window.location.origin);
    if (isRelative || isSameOrigin) {
      const token = getCsrfToken();
      if (token) {
        config.headers["X-CSRF-Token"] = token;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Detect session expiration and redirect to login.
// When a Devise session expires, requests without Accept: application/json
// get 302-redirected to /users/sign_in, and axios follows the redirect,
// receiving the HTML login page as the response body (status 200).
// Requests with Accept: application/json get a 401 status.
function isSessionExpired(response) {
  // Check if we were redirected to the sign-in page
  const responseURL = response.request?.responseURL || "";
  if (responseURL.includes("/users/sign_in")) return true;

  // Check if an API request returned the sign-in HTML page after a redirect.
  // We require the response URL to end up at the sign-in path to avoid
  // false positives on mobile (Safari returns its own HTML error pages for
  // network failures, which would otherwise incorrectly log the user out).
  const contentType = response.headers?.["content-type"] || "";
  const requestURL = response.config?.url || "";
  if (
    requestURL.startsWith("/api/") &&
    contentType.includes("text/html") &&
    typeof response.data === "string" &&
    responseURL.includes("/users/sign_in")
  )
    return true;

  return false;
}

let isRedirecting = false;

function redirectToLogin() {
  if (isRedirecting) return;
  isRedirecting = true;
  window.location.href = "/users/sign_in";
}

// Add response interceptor to the default axios instance so ALL imports
// of axios get session expiration handling (most of the codebase uses
// `import axios from "axios"` directly rather than this custom instance).
axios.interceptors.response.use(
  (response) => {
    if (isSessionExpired(response)) {
      redirectToLogin();
      return new Promise(() => {}); // Prevent further processing
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      // Only redirect for internal API requests, not third-party
      if (url.startsWith("/api/")) {
        redirectToLogin();
        return new Promise(() => {}); // Prevent further processing
      }
    }
    return Promise.reject(error);
  },
);

// Also add to the custom api instance
api.interceptors.response.use(
  (response) => {
    if (isSessionExpired(response)) {
      redirectToLogin();
      return new Promise(() => {});
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      redirectToLogin();
      return new Promise(() => {});
    }
    return Promise.reject(error);
  },
);

// Extract user-facing messages from Rails/Grape validation error responses.
// Handles:
//   { field: ["msg"] }                  — model.errors directly
//   { errors: { field: ["msg"] } }      — wrapped model.errors
//   { errors: ["msg1", "msg2"] }        — array of messages
//   { error: "msg" }                    — single string (Grape / manual)
function extractValidationErrors(data) {
  if (!data || typeof data !== "object") return null;

  if (typeof data.error === "string") return [data.error];

  if (Array.isArray(data.errors)) return data.errors.filter(Boolean);

  if (data.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat().filter(Boolean);
  }

  // Bare model.errors: { field: ["msg"] }
  const values = Object.values(data).flat();
  if (values.every((v) => typeof v === "string")) return values;

  return null;
}

// Intercept 422 / 400 responses on both instances and attach a
// `validationErrors` array to the thrown error so callers can simply:
//   } catch (error) { if (error.validationErrors) showAlert(...) }
function attachValidationErrors(error) {
  const status = error.response?.status;
  if (status === 422 || status === 400) {
    const messages = extractValidationErrors(error.response.data);
    if (messages?.length) {
      error.validationErrors = messages;
    }
  }
  return Promise.reject(error);
}

axios.interceptors.response.use(undefined, attachValidationErrors);
api.interceptors.response.use(undefined, attachValidationErrors);

// True when the failure means the request ran out of time: our proxy or the
// API answered 408/503/504, or the browser gave up on the connection.
function isTimeoutError(error) {
  if (axios.isCancel(error)) return false;
  if ([408, 503, 504].includes(error?.response?.status)) return true;
  return error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT";
}

export { getCsrfToken, isTimeoutError };
export default api;
